import React, { useState, useRef, useEffect } from 'react';
import { Html5QrcodeScanner } from 'html5-qrcode';
import toast from 'react-hot-toast';
import { config } from '../../config';

export default function DebitBus() {
  const [codePin, setCodePin] = useState('');
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const inputRef = useRef(null);
  const lastPinRef = useRef('');
  const scannerRef = useRef(null);

  const API_BASE = config.apiBase;

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // ✅ Débit automatique quand 6 chiffres sont saisis
  useEffect(() => {
    if (!/^[0-9]{6}$/.test(codePin)) return;
    if (processing) return;
    if (lastPinRef.current === codePin) return;

    const timer = setTimeout(() => {
      debiterBus(codePin);
    }, 350);

    return () => clearTimeout(timer);
  }, [codePin]);

  // Initialiser le scanner
  useEffect(() => {
    if (scanning) {
      const timer = setTimeout(() => {
        try {
          if (scannerRef.current) {
            scannerRef.current.clear().catch(console.error);
          }
          const scanner = new Html5QrcodeScanner(
            "qr-reader",
            {
              qrbox: { width: 250, height: 250 },
              fps: 10,
            },
            false
          );
          scanner.render(onScanSuccess, onScanError);
          scannerRef.current = scanner;
        } catch (error) {
          console.error("Erreur scanner:", error);
          toast.error("Erreur d'initialisation de la caméra");
          setScanning(false);
        }
      }, 200);

      return () => {
        clearTimeout(timer);
        if (scannerRef.current) {
          scannerRef.current.clear().catch(console.error);
          scannerRef.current = null;
        }
      };
    }
  }, [scanning]);

  const onScanSuccess = (decodedText) => {
    const pin = decodedText.replace(/\D/g, '').substring(0, 6);
    
    // ✅ Exactement 6 chiffres
    if (pin.length === 6) {
      setCodePin(pin);
      setScanning(false);
      toast.success(`Code PIN scanné : ${pin}`);
      inputRef.current?.focus();
    } else {
      toast.error('QR code invalide (6 chiffres requis)');
    }
  };

  const onScanError = (error) => {
    // Ignorer les erreurs normales de scan
  };

  const debiterBus = async (pin) => {
    setProcessing(true);
    setResult(null);
    lastPinRef.current = pin;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/pat/debiter_bus.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code_pin: pin }),
      });

      const data = await response.json();

      if (data.success) {
        setResult({
          type: 'success',
          title: 'Débit bus réussi',
          message: `${data.etudiant} a été débité de ${Number(data.montant).toLocaleString()} FCFA.`,
          solde: data.nouveau_solde_crou,
        });
        toast.success('Bus débité avec succès');
      } else {
        setResult({
          type: 'error',
          title: data.code === 'SOLDE_INSUFFISANT' ? 'Compte insuffisant' : 'Débit refusé',
          message: data.message || 'Impossible de débiter ce compte.',
          solde: data.solde_crou,
          montantRequis: data.montant_requis,
        });
        toast.error(data.message || 'Débit refusé');
      }
    } catch (error) {
      console.error(error);
      setResult({
        type: 'error',
        title: 'Erreur de connexion',
        message: 'Impossible de contacter le serveur.',
      });
      toast.error('Erreur de connexion');
    } finally {
      setProcessing(false);
      setTimeout(() => {
        setCodePin('');
        lastPinRef.current = '';
        inputRef.current?.focus();
      }, 900);
    }
  };

  const reset = () => {
    setCodePin('');
    setResult(null);
    lastPinRef.current = '';
    inputRef.current?.focus();
  };

  // ✅ Fonction pour le débit manuel (optionnel)
  const executeManualDebit = () => {
    if (codePin.length === 6 && !processing) {
      debiterBus(codePin);
    } else {
      toast.error("Code PIN invalide (6 chiffres requis)");
    }
  };

  return (
    <div className="min-h-screen bg-white p-4 md:p-6">
      <div className="mx-auto max-w-4xl">
        <div className="mb-6">
          <h1 className="text-3xl font-black text-blue-800">Débit Bus</h1>
          <p className="mt-1 text-sm text-slate-500">
            Montant automatique : 80 FCFA. Le débit se lance dès que le code PIN (6 chiffres) est saisi.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[360px_1fr]">
          <div className="border border-blue-100 bg-white p-6 shadow-lg">
            <div className="mb-5 border-l-4 border-blue-700 bg-blue-50 p-4">
              <div className="text-sm font-black uppercase text-blue-700">Transport CROU</div>
              <div className="mt-1 text-3xl font-black text-slate-900">80 FCFA</div>
            </div>

            <label className="mb-2 block text-sm font-bold text-slate-700">Code PIN étudiant (6 chiffres)</label>

            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="password"
                value={codePin}
                onChange={(e) => setCodePin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength="6"
                placeholder="••••••"
                disabled={processing}
                className="w-full border-2 border-blue-100 bg-blue-50 px-5 py-5 text-center font-mono text-3xl font-black tracking-[0.45em] text-slate-900 outline-none focus:border-blue-700 focus:bg-white"
              />
              <button
                onClick={() => setScanning(true)}
                disabled={processing}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-50"
                title="Scanner le QR code"
              >
                📷 Scanner
              </button>
            </div>

            <p className="mt-3 text-center text-xs text-slate-400">Le traitement démarre automatiquement après 6 chiffres</p>

            <button
              type="button"
              onClick={reset}
              className="mt-5 w-full bg-slate-100 px-4 py-3 text-sm font-black text-slate-600 hover:bg-slate-200"
            >
              Réinitialiser
            </button>
          </div>

          <div className="flex min-h-[340px] items-center justify-center border border-slate-100 bg-white p-6 shadow-lg">
            {processing ? (
              <div className="text-center">
                <div className="mx-auto mb-4 h-14 w-14 animate-spin rounded-full border-4 border-blue-100 border-t-blue-700"></div>
                <h2 className="text-2xl font-black text-slate-800">Débit en cours...</h2>
                <p className="mt-2 text-sm text-slate-400">Vérification du compte CROU.</p>
              </div>
            ) : !result ? (
              <div className="text-center">
                <div className="mb-4 text-6xl">🚌</div>
                <h2 className="text-2xl font-black text-slate-800">En attente du code PIN</h2>
                <p className="mt-2 text-sm text-slate-400">Saisissez ou scannez le code PIN (6 chiffres)</p>
              </div>
            ) : result.type === 'success' ? (
              <div className="w-full max-w-md text-center">
                <div className="mx-auto mb-5 flex h-24 w-24 items-center justify-center rounded-full bg-green-100 text-5xl text-green-700">✓</div>
                <h2 className="text-3xl font-black text-green-700">{result.title}</h2>
                <p className="mt-3 text-base font-semibold text-slate-600">{result.message}</p>
                <div className="mt-5 bg-green-50 p-4">
                  <div className="text-xs font-black uppercase text-green-600">Nouveau solde CROU</div>
                  <div className="mt-1 text-3xl font-black text-green-700">{Number(result.solde || 0).toLocaleString()} FCFA</div>
                </div>
              </div>
            ) : (
              <div className="w-full max-w-md text-center">
                <div className="mx-auto mb-5 flex h-24 w-24 items-center justify-center rounded-full bg-red-100 text-5xl font-black text-red-700">×</div>
                <h2 className="text-3xl font-black text-red-700">{result.title}</h2>
                <p className="mt-3 text-base font-semibold text-slate-600">{result.message}</p>
                {result.montantRequis && (
                  <div className="mt-5 bg-red-50 p-4">
                    <div className="text-xs font-black uppercase text-red-500">Solde disponible</div>
                    <div className="mt-1 text-2xl font-black text-red-700">{Number(result.solde || 0).toLocaleString()} FCFA</div>
                    <div className="mt-2 text-sm font-bold text-red-500">Montant requis : {Number(result.montantRequis).toLocaleString()} FCFA</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal Scanner */}
      {scanning && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setScanning(false)}>
          <div className="bg-white rounded-xl max-w-md w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="font-bold text-lg">Scanner la carte étudiante</h3>
              <button onClick={() => setScanning(false)} className="text-2xl">&times;</button>
            </div>
            <div id="qr-reader" style={{ width: '100%', minHeight: '400px' }}></div>
            <p className="text-center p-4 text-gray-600">Positionnez le QR code dans le cadre</p>
          </div>
        </div>
      )}
    </div>
  );
}